/**
 * Utility functions for merging multiple student plans into a single overview plan
 */

/**
 * Merges multiple student plans into a single overview plan
 * @param {Array} plans - Array of plan objects with data.panelGroups
 * @returns {Object} - Merged plan object
 */
export const mergePlansToOverview = (plans) => {
  if (!plans || plans.length === 0) {
    return {
      name: 'Übersichtsplan',
      description: 'Kein Plan verfügbar',
      data: {
        panelGroups: [],
        startDate: new Date().toISOString()
      },
      merged: true,
      sourcePlans: []
    };
  }

  if (plans.length === 1) {
    // Wenn nur ein Plan vorhanden ist, geben wir ihn als Übersichtsplan zurück
    const plan = plans[0];
    return {
      ...plan,
      name: `Übersichtsplan: ${plan.name}`,
      description: `Einzelplan von ${plan.user_id}`,
      merged: true,
      sourcePlans: [{ name: plan.name, user_id: plan.user_id, taskCount: plan.assigned_tasks_count || 0 }]
    };
  }

  // Mehrere Pläne zusammenführen mit automatischer Gruppierung
  const groupMap = new Map(); // Map für gleichnamige Gruppen
  const sourcePlans = [];
  let totalTasks = 0;
  let earliestStartDate = null;

  // Sammle Informationen über die Quellpläne
  plans.forEach(plan => {
    if (plan.name && plan.user_id !== undefined) {
      sourcePlans.push({
        name: plan.name,
        user_id: plan.user_id,
        taskCount: plan.assigned_tasks_count || 0
      });
      totalTasks += plan.assigned_tasks_count || 0;
    }

    // Frühestes Startdatum finden
    if (plan.data && plan.data.startDate) {
      const planStartDate = new Date(plan.data.startDate);
      if (!earliestStartDate || planStartDate < earliestStartDate) {
        earliestStartDate = planStartDate;
      }
    }

    // Panel-Gruppen automatisch zusammenführen
    if (plan.data && plan.data.panelGroups) {
      plan.data.panelGroups.forEach((group, groupIndex) => {
        const groupTitle = group.title || `Gruppe ${groupIndex + 1}`;
        
        // Prüfe ob eine Gruppe mit diesem Namen bereits existiert
        if (groupMap.has(groupTitle)) {
          // Füge Panels zur existierenden Gruppe hinzu
          const existingGroup = groupMap.get(groupTitle);
          
          // Merge panels automatisch
          if (group.panels) {
            group.panels.forEach(panel => {
              const panelTitle = panel.title || 'Unbenanntes Panel';
              
              // Suche nach Panel mit gleichem Namen in der existierenden Gruppe
              const existingPanelIndex = existingGroup.panels.findIndex(p => 
                (p.title || 'Unbenanntes Panel') === panelTitle
              );
              
              if (existingPanelIndex >= 0) {
                // Panel mit gleichem Namen gefunden - MovableBars (Tasks) zusammenführen
                const existingPanel = existingGroup.panels[existingPanelIndex];
                if (panel.movableBars) {
                  panel.movableBars.forEach(bar => {
                    existingPanel.movableBars.push({
                      ...bar,
                      id: `${plan.name}_${bar.id || Math.random()}`, // Eindeutige Task-ID
                      sourcePlan: plan.name,
                      sourcePlanUser: plan.user_id
                    });
                  });
                }
              } else {
                // Neues Panel hinzufügen
                existingGroup.panels.push({
                  ...panel,
                  id: `${plan.name}_${panel.id || Math.random()}`,
                  sourcePlan: plan.name,
                  sourcePlanUser: plan.user_id,
                  movableBars: panel.movableBars ? panel.movableBars.map(bar => ({
                    ...bar,
                    originalId: bar.id, // Behalte die ursprüngliche ID für Keys bei
                    id: `${plan.name}_${bar.id || Math.random()}`,
                    sourcePlan: plan.name,
                    sourcePlanUser: plan.user_id
                  })) : []
                });
              }
            });
          }

          // Source Plans für die Gruppe aktualisieren
          if (!existingGroup.sourcePlans.includes(plan.name)) {
            existingGroup.sourcePlans.push(plan.name);
          }
          
        } else {
          // Neue Gruppe erstellen
          const newGroup = {
            ...group,
            id: `merged_${groupTitle}_${Math.random()}`,
            title: groupTitle,
            sourcePlans: [plan.name], // Liste der Quellpläne für diese Gruppe
            panels: group.panels ? group.panels.map(panel => ({
              ...panel,
              id: `${plan.name}_${panel.id || Math.random()}`,
              sourcePlan: plan.name,
              sourcePlanUser: plan.user_id,
              movableBars: panel.movableBars ? panel.movableBars.map(bar => ({
                ...bar,
                originalId: bar.id, // Behalte die ursprüngliche ID für Keys bei
                id: `${plan.name}_${bar.id || Math.random()}`,
                sourcePlan: plan.name,
                sourcePlanUser: plan.user_id
              })) : []
            })) : []
          };
          
          groupMap.set(groupTitle, newGroup);
        }
      });
    }
  });

  // Gruppen in Array konvertieren
  const mergedGroups = Array.from(groupMap.values());

  // Gruppen nach Position sortieren (falls vorhanden)
  mergedGroups.sort((a, b) => {
    const aPos = a.position || 0;
    const bPos = b.position || 0;
    if (aPos !== bPos) {
      return aPos - bPos;
    }
    // Sekundäre Sortierung nach Planname
    return (a.sourcePlan || '').localeCompare(b.sourcePlan || '');
  });

  const planNames = sourcePlans.map(p => p.name).join(', ');
  const userNames = [...new Set(sourcePlans.map(p => p.user_id))].join(', ');

  return {
    name: 'Übersichtsplan',
    description: `Zusammenführung von ${plans.length} Plänen: ${planNames}`,
    version: 'merged',
    id: 'overview_plan',
    base_name: 'overview',
    assigned_tasks_count: totalTasks,
    user_id: userNames,
    data: {
      panelGroups: mergedGroups,
      startDate: earliestStartDate ? earliestStartDate.toISOString() : new Date().toISOString()
    },
    merged: true,
    sourcePlans: sourcePlans
  };
};

/**
 * Creates a summary description for the overview plan
 * @param {Array} sourcePlans - Array of source plan info objects
 * @returns {string} - Summary description
 */
export const createOverviewDescription = (sourcePlans) => {
  if (!sourcePlans || sourcePlans.length === 0) {
    return 'Kein Plan verfügbar';
  }

  if (sourcePlans.length === 1) {
    const plan = sourcePlans[0];
    return `Plan "${plan.name}" von ${plan.user_id} (${plan.taskCount} Tasks)`;
  }

  const totalTasks = sourcePlans.reduce((sum, plan) => sum + plan.taskCount, 0);
  const teachers = [...new Set(sourcePlans.map(p => p.user_id))];
  
  return `${sourcePlans.length} Pläne von ${teachers.join(', ')} - Insgesamt ${totalTasks} Tasks`;
};

/**
 * Generates merge statistics for the overview plan
 * @param {Object} overviewPlan - The merged overview plan
 * @returns {Object} - Statistics about the merge process
 */
export const getMergeStatistics = (overviewPlan) => {
  if (!overviewPlan || !overviewPlan.merged || !overviewPlan.data) {
    return {
      totalGroups: 0,
      totalPanels: 0,
      totalTasks: 0,
      mergedGroups: 0,
      mergedPanels: 0,
      sourcePlans: []
    };
  }

  const groups = overviewPlan.data.panelGroups || [];
  let totalPanels = 0;
  let totalTasks = 0;
  let mergedGroups = 0;
  let mergedPanels = 0;

  groups.forEach(group => {
    // Zähle als zusammengeführt wenn mehr als ein Quellplan
    if (group.sourcePlans && group.sourcePlans.length > 1) {
      mergedGroups++;
    }

    if (group.panels) {
      totalPanels += group.panels.length;
      
      group.panels.forEach(panel => {
        // Zähle MovableBars (Tasks) im Panel
        if (panel.movableBars) {
          totalTasks += panel.movableBars.length;
          
          // Prüfe ob Panel MovableBars aus verschiedenen Quellplänen hat (zusammengeführt)
          const uniqueSources = [...new Set(panel.movableBars.map(bar => bar.sourcePlan))];
          if (uniqueSources.length > 1) {
            mergedPanels++;
          }
        }
      });
    }
  });

  return {
    totalGroups: groups.length,
    totalPanels,
    totalTasks,
    mergedGroups,
    mergedPanels,
    sourcePlans: overviewPlan.sourcePlans || []
  };
};

/**
 * Filters tasks from overview plan by source plan
 * @param {Object} overviewPlan - The merged overview plan
 * @param {string} sourcePlanName - Name of the source plan to filter by
 * @returns {Object} - Filtered plan showing only tasks from specified source
 */
export const filterOverviewBySource = (overviewPlan, sourcePlanName) => {
  if (!overviewPlan || !overviewPlan.merged || !sourcePlanName) {
    return overviewPlan;
  }

  const filteredGroups = overviewPlan.data.panelGroups
    .map(group => {
      // Prüfe ob die Gruppe Tasks/Panels aus dem gewünschten Quellplan enthält
      const hasSourceContent = group.sourcePlans && group.sourcePlans.includes(sourcePlanName);
      
      if (!hasSourceContent) {
        return null; // Gruppe nicht relevant für diesen Quellplan
      }
      
      return {
        ...group,
        // Filtere Panels nach Quellplan
        panels: group.panels ? group.panels
          .filter(panel => panel.sourcePlan === sourcePlanName)
          .map(panel => ({
            ...panel,
            movableBars: panel.movableBars ? panel.movableBars.filter(bar => bar.sourcePlan === sourcePlanName) : []
          })) : []
      };
    })
    .filter(group => group !== null); // Entferne irrelevante Gruppen

  const sourcePlan = overviewPlan.sourcePlans.find(p => p.name === sourcePlanName);
  
  return {
    ...overviewPlan,
    name: sourcePlanName,
    description: sourcePlan ? `Einzelansicht: ${sourcePlan.name} von ${sourcePlan.user_id}` : sourcePlanName,
    data: {
      ...overviewPlan.data,
      panelGroups: filteredGroups
    },
    filtered: true,
    filterSource: sourcePlanName
  };
};